<template>
  <div>
    <div :class="{'tiptap-editor-wrap': userCanEdit}">
      <template v-if="userCanEdit && !previewDraftParam">
        <div class="tiptap-editor-controls-enable">
          <b-button
            v-if="tiptapEditable"
            class="btn-primary"
            :title="'preview ' + containerKey"
            @click="checkUnsavedContentEditorClose(contentStatusDefault)"
          >
            <i class="icon"><icon-times /></i>
          </b-button>
          <b-button
            v-else
            class="btn-primary"
            :title="tiptapPageLocked ? lockedEditableTitle : initEditableTitle"
            :disabled="tiptapPageLocked"
            @click="tiptapInitEditableWrapper(tiptapEditableContentStatus)"
          >
            <i class="icon"><icon-pencil-alt /></i>
          </b-button>
        </div>
        <div
          v-if="tiptapEditable"
          class="tiptap-editor-controls container"
        >
          <div class="row">
            <div class="col-12">
              <p class="mb-0">
                <template v-if="tiptapEditableContentStatus === 'draft' && tiptapContentDraftPublished">
                  {{ $t('tiptap:contentStatus.draft', {
                    user: tiptapContentStore[containerKey].draft.contentLastchangedBy,
                    dateTime: tiptapContentStore[containerKey].draft.contentLastchanged,
                  }) }}
                </template>
                <template v-else-if="tiptapEditableContentStatus === 'live' && tiptapContentLivePublished">
                  {{ $t('tiptap:contentStatus.live', {
                    user: tiptapContentStore[containerKey].live.contentLastchangedBy,
                    dateTime: tiptapContentStore[containerKey].live.contentLastchanged,
                  }) }}
                </template>
              </p>
              <div class="row">
                <div class="col-6">
                  <b-form-group>
                    <b-form-radio-group
                      v-model="tiptapEditableContentStatus"
                      :options="contentStatusOptions"
                      buttons
                      button-variant="light"
                      @input="tiptapInitEditableWrapper(tiptapEditableContentStatus)"
                    />
                    <a
                      v-if="tiptapEditableContentStatus === 'draft'"
                      class="link-underline"
                      :href="currentRouteAsDraft"
                      target="_blank"
                      rel="noopener"
                      :title="$t('tiptap:btn.previewDraftTitle')"
                    >
                      {{ $t('tiptap:btn.previewDraft') }}
                    </a>
                  </b-form-group>
                </div>
                <div class="col-6 text-right">
                  <b-form-group>
                    <b-button
                      v-if="tiptapEditableContentStatus === 'draft'"
                      class="btn btn-secondary"
                      :title="$t('tiptap:btn.ignoreDraftTitle')"
                      @click="tiptapInitEditableWrapper('draft', 'live')"
                    >
                      {{ $t('tiptap:btn.ignoreDraft') }}
                    </b-button>
                    <b-button
                      v-if="tiptapEditableContentStatus === 'draft'"
                      class="btn btn-secondary"
                      :title="$t('tiptap:btn.publishTitle')"
                      @click="tiptapContentSetWrapper('live', 'save')"
                    >
                      {{ $t('tiptap:btn.publish') }}
                    </b-button>
                    <b-button
                      class="btn-secondary"
                      @click="tiptapContentSetWrapper(tiptapEditableContentStatus, 'save')"
                    >
                      {{ $t('tiptap:btn.save') }}
                    </b-button>
                    <b-button
                      class="btn-secondary"
                      @click.prevent="tiptapContentDeleteWrapper"
                    >
                      {{ $t('tiptap:btn.delete') }}
                    </b-button>
                    <modal
                      name="tiptapContentDeleteModal"
                      :scrollable="true"
                      height="auto"
                      width="600px"
                      @close="tiptapContentDeleteWrapperClose"
                    >
                      <b-button
                        class="modal-close"
                        variant="light"
                        @click="tiptapContentDeleteWrapperClose"
                      >
                        <i class="icon"><icon-times /></i>
                      </b-button>
                      <div class="modal-header">
                        <h4 class="text-center mb-4">
                          {{ $t('tiptap:deleteModal.headline') }}
                        </h4>
                      </div>
                      <div class="modal-body m-4">
                        <div class="container text-left">
                          <p>
                            {{ $t('tiptap:contentStatus.' + tiptapEditableContentStatus, {
                              user: tiptapContentStore[containerKey][tiptapEditableContentStatus].contentLastchangedBy,
                              dateTime: tiptapContentStore[containerKey][tiptapEditableContentStatus].contentLastchanged,
                            }) }}
                          </p>
                          <b-button
                            class="btn-secondary mr-2"
                            @click.prevent="tiptapContentSetWrapper(tiptapEditableContentStatus, 'delete')"
                          >
                            {{ $t('tiptap:deleteModal.yes') }}
                          </b-button>
                          <b-button
                            class="btn-secondary"
                            @click.prevent="tiptapContentDeleteWrapperClose"
                          >
                            {{ $t('tiptap:deleteModal.no') }}
                          </b-button>
                        </div>
                      </div>
                    </modal>
                  </b-form-group>
                </div>
              </div>
            </div>
            <div class="col-12">
              <b-button
                variant="light"
                :title="$t('tiptap:btn.undo')"
                @click="tiptapEditable.chain().focus().undo().run()"
              >
                ↶
              </b-button>
              <b-button
                variant="light"
                :title="$t('tiptap:btn.redo')"
                @click="tiptapEditable.chain().focus().redo().run()"
              >
                ↷
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('bold') }"
                :title="$t('tiptap:btn.bold')"
                @click="tiptapEditable.chain().focus().toggleBold().run()"
              >
                <span class="font-weight-bold">B</span>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('italic') }"
                :title="$t('tiptap:btn.italic')"
                @click="tiptapEditable.chain().focus().toggleItalic().run()"
              >
                <i class="icon"><icon-italic /></i>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('strike') }"
                :title="$t('tiptap:btn.strike')"
                @click="tiptapEditable.chain().focus().toggleStrike().run()"
              >
                <i class="icon"><icon-strikethrough /></i>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive({ class: 'text-left' }) }"
                :title="$t('tiptap:btn.alignLeft')"
                @click="tiptapContentSetAlignmentClass('text-left')"
              >
                <i class="icon"><icon-align-left /></i>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive({ class: 'text-center' }) }"
                :title="$t('tiptap:btn.alignCenter')"
                @click="tiptapContentSetAlignmentClass('text-center')"
              >
                <i class="icon"><icon-align-center /></i>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive({ class: 'text-right' }) }"
                :title="$t('tiptap:btn.alignRight')"
                @click="tiptapContentSetAlignmentClass('text-right')"
              >
                <i class="icon"><icon-align-right /></i>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive({ class: 'text-justify' }) }"
                :title="$t('tiptap:btn.alignJustify')"
                @click="tiptapContentSetAlignmentClass('text-justify')"
              >
                <i class="icon"><icon-align-justify /></i>
              </b-button>
              <b-button
                variant="light"
                :title="$t('tiptap:btn.alignUnset')"
                @click="tiptapContentSetAlignmentClass('')"
              >
                <i class="icon"><icon-align-slash /></i>
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('heading', { level: 1 }) }"
                @click="tiptapEditable.chain().focus().toggleHeading({ level: 1 }).run()"
              >
                h1
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('heading', { level: 2 }) }"
                @click="tiptapEditable.chain().focus().toggleHeading({ level: 2 }).run()"
              >
                h2
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('heading', { level: 3 }) }"
                @click="tiptapEditable.chain().focus().toggleHeading({ level: 3 }).run()"
              >
                h3
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('heading', { level: 4 }) }"
                @click="tiptapEditable.chain().focus().toggleHeading({ level: 4 }).run()"
              >
                h4
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('heading', { level: 5 }) }"
                @click="tiptapEditable.chain().focus().toggleHeading({ level: 5 }).run()"
              >
                h5
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('heading', { level: 6 }) }"
                @click="tiptapEditable.chain().focus().toggleHeading({ level: 6 }).run()"
              >
                h6
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('bulletList') }"
                :title="$t('tiptap:btn.bulletList')"
                @click="tiptapEditable.chain().focus().toggleBulletList().run()"
              >
                • list
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('orderedList') }"
                :title="$t('tiptap:btn.orderedList')"
                @click="tiptapEditable.chain().focus().toggleOrderedList().run()"
              >
                1. list
              </b-button>
              <b-button
                variant="light"
                :class="{ 'is-active': tiptapEditable.isActive('paragraph') }"
                :title="$t('tiptap:btn.paragraph')"
                @click="tiptapEditable.chain().focus().setParagraph().run()"
              >
                &lt;p&gt;
              </b-button>
              <b-button
                variant="light"
                :title="$t('tiptap:btn.horizontalRule')"
                @click="tiptapEditable.chain().focus().setHorizontalRule().run()"
              >
                &lt;hr&gt;
              </b-button>
              <b-button
                variant="light"
                :title="$t('tiptap:btn.lineBreak')"
                @click="tiptapEditable.chain().focus().setHardBreak().run()"
              >
                &lt;br&gt;
              </b-button>
            </div>
            <div class="col-12 d-flex flex-wrap">
              <standard-link-btn
                @insert-content="tiptapContentInsert"
              />
              <button-link-btn
                @insert-content="tiptapContentInsert"
              />
              <image-comp-btn
                @insert-content="tiptapContentInsert"
              />
              <!-- deactivated. Needs more work before re-activating -->
              <row-col-btn
                v-if="false"
                @insert-content="tiptapContentInsert"
              />
              <content-link-list-btn
                @insert-content="tiptapContentInsert"
              />
              <quotation-block-btn
                @insert-content="tiptapContentInsert"
              />
              <last-seen-objects-btn
                @insert-content="tiptapContentInsert"
              />
              <faq-list-btn
                @insert-content="tiptapContentInsert"
              />
              <custom-object-teaser-btn
                @insert-content="tiptapContentInsert"
              />
            </div>
          </div>
        </div>
      </template>
      <editor-content
        v-if="userCanEdit && tiptapEditable"
        class="mt-3"
        :editor="tiptapEditable"
      />
      <template v-else>
        <div
          v-for="(contentData, contentKey) in tiptapStatic"
          :key="contentKey"
        >
          <component
            :is="tiptapComponentKebabize(contentData.type)"
            v-if="contentData.type"
            v-bind="contentData.attrs"
          />
          <div
            v-else
            v-dompurify-html="contentData"
          />
        </div>
      </template>
    </div>
    <template v-if="showDefaultContent">
      <div v-dompurify-html="defaultContent" />
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { EditorContent } from '@tiptap/vue-2';

import {
  BButton,
  BAlert,
  BFormGroup,
  BFormRadioGroup,
  BFormCheckbox,
} from 'bootstrap-vue';
import UserApi from 'mixins/UserApi';
import setupTipTapApi from 'mixins/composition/TiptapApi';

import IconPencilAlt from 'ICON:IconPencilAlt';
import IconTimes from 'ICON:IconTimes';
import IconBold from 'ICON:IconBold';
import IconItalic from 'ICON:IconItalic';
import IconStrikethrough from 'ICON:IconStrikethrough';
import IconAlignLeft from 'ICON:IconAlignLeft';
import IconAlignCenter from 'ICON:IconAlignCenter';
import IconAlignRight from 'ICON:IconAlignRight';
import IconAlignJustify from 'ICON:IconAlignJustify';
import IconAlignSlash from 'ICON:IconAlignSlash';
import ImageCompBtn from 'components/tiptap/ImageCompBtn.vue';
import StandardLinkBtn from 'components/tiptap/StandardLinkBtn.vue';
import RowColBtn from 'components/tiptap/RowColBtn.vue';
import LastSeenObjectsBtn from 'components/tiptap/LastSeenObjectsBtn.vue';
import CustomObjectTeaserBtn from 'components/tiptap/CustomObjectTeaserBtn.vue';
import ContentLinkListBtn from 'components/tiptap/ContentLinkListBtn.vue';
import QuotationBlockBtn from 'components/tiptap/QuotationBlockBtn.vue';
import ButtonLinkBtn from 'components/tiptap/ButtonLinkBtn.vue';
import FaqListBtn from 'components/tiptap/FaqListBtn.vue';

export default {
  name: 'TiptapContainer',
  components: {
    IconPencilAlt,
    IconTimes,
    IconBold,
    IconItalic,
    IconStrikethrough,
    IconAlignLeft,
    IconAlignCenter,
    IconAlignRight,
    IconAlignJustify,
    IconAlignSlash,
    BButton,
    BAlert,
    BFormGroup,
    BFormRadioGroup,
    BFormCheckbox,
    EditorContent,
    ImageCompBtn,
    StandardLinkBtn,
    RowColBtn,
    LastSeenObjectsBtn,
    CustomObjectTeaserBtn,
    ContentLinkListBtn,
    QuotationBlockBtn,
    ButtonLinkBtn,
    FaqListBtn
  },
  i18nOptions: {
    namespaces: [
      'tiptap',
      'date',
    ],
  },
  mixins: [
    UserApi,
  ],
  props: {
    containerKey: {
      type: String,
      required: true,
    },
    defaultContent: {
      type: String,
      default: '',
    },
  },
  setup(props, { root }) {
    const tiptapApiComposition = setupTipTapApi({
      store: root.$store,
      route: root.$route,
      bvToast: root.$bvToast,
      i18next: root.$i18n.i18next,
      containerKey: props.containerKey,
    });

    return {
      componentShorttagsComponents: tiptapApiComposition.componentShorttagsComponents,
      tiptapStatic: tiptapApiComposition.tiptapStatic,
      tiptapStaticInit: tiptapApiComposition.tiptapStaticInit,
      tiptapEditable: tiptapApiComposition.tiptapEditable,
      tiptapEditableInit: tiptapApiComposition.tiptapEditableInit,
      tiptapEditableContentStatus: tiptapApiComposition.tiptapEditableContentStatus,
      tiptapEditableChanged: tiptapApiComposition.tiptapEditableChanged,
      tiptapContentGet: tiptapApiComposition.tiptapContentGet,
      tiptapContentSet: tiptapApiComposition.tiptapContentSet,
      tiptapContentLockToggle: tiptapApiComposition.tiptapContentLockToggle,
      tiptapContentStore: tiptapApiComposition.tiptapContentStore,
      tiptapContentLivePublished: tiptapApiComposition.tiptapContentLivePublished,
      tiptapContentDraftPublished: tiptapApiComposition.tiptapContentDraftPublished,
      tiptapComponentKebabize: tiptapApiComposition.tiptapComponentKebabize,
      tiptapContentInsert: tiptapApiComposition.tiptapContentInsert,
      tiptapContentSetAlignmentClass: tiptapApiComposition.tiptapContentSetAlignmentClass,
    };
  },
  data() {
    return {
      initEditableTitle: this.$t('tiptap:btn.initEditableTitle', { containerKey: this.containerKey }),
      lockedEditableTitle: '',
      contentStatusOptions: [
        { text: this.$t('tiptap:draft'), value: 'draft' },
        { text: this.$t('tiptap:live'), value: 'live' },
      ],
      preventLinkClickTimeout: false,
    };
  },
  computed: {
    ...mapGetters({
      userDataStatus: 'userDataStatus',
    }),
    currentRoutePath() {
      return this.$route?.path;
    },
    userCanEdit() {
      return typeof document !== 'undefined' && this.userDataStatus === 'userSaved' && this.UserApi_checkPermission('cms.edit');
    },
    currentRouteAsDraft() {
      const routeParams = { ...this.$route.query, previewDraft: 'show' };
      const routeParamsStr = new URLSearchParams(routeParams).toString();
      return this.$route.path + (routeParamsStr ? `?${routeParamsStr}` : '');
    },
    previewDraftParam() {
      return this.$route?.query?.previewDraft === 'show' ? 'show' : false;
    },
    // default content status to render (draft or live)
    // also mark if draft or live should be on SSR
    // depends on GET param previewDraft
    contentStatusDefault() {
      if (this.previewDraftParam) {
        return 'draft';
      }
      return 'live';
    },
    // lock every tiptap editor on page if at least one container has status locked
    tiptapPageLocked() {
      let returnVal = null;
      if (this.tiptapContentStore) {
        Object.values(this.tiptapContentStore).forEach((containerKeyData) => {
          const containerKeyDataArr = Object.values(containerKeyData);
          containerKeyDataArr.forEach((containerKeyDataSub) => {
            // do not lock if current user is the one who locked content
            if (containerKeyDataSub.contentLockerID && containerKeyDataSub.contentLockerID !== this.UserApi_accountData?.accountID) {
              returnVal = containerKeyDataSub.contentLocker;
            }
          });
        });
      }
      return returnVal;
    },
    showDefaultContent() {
      return !this.tiptapContentLivePublished && !this.tiptapEditable;
    },
    contentStatusTranslated() {
      return this.$t(`tiptap:${this.tiptapEditableContentStatus}`);
    }
  },
  watch: {
    tiptapEditableChanged(newVal) {
      this.disableContentLinks();
      if (newVal) {
        window.addEventListener('beforeunload', this.checkUnsavedContentBeforeunload);
      } else {
        window.removeEventListener('beforeunload', this.checkUnsavedContentBeforeunload);
      }
    },
    userCanEdit(newVal) {
      if (newVal) {
        // get api content including drafts when user has permissions to edit
        this.tiptapContentGet({ previewDraft: this.previewDraftParam }).then(() => {
          this.lockedEditableTitle = this.$t('tiptap:btn.lockedEditableTitle', {
            user: this.tiptapPageLocked,
          });
        });
      }
    },
    currentRoutePath() {
      this.tiptapInitStaticWrapper('live');
    },
    tiptapEditable() {
      this.disableContentLinks();
    }
  },
  created() {
    // append components used in shorttag to template (needed for ssr)
    Object.keys(this.componentShorttagsComponents).forEach((componentName) => {
      this.$options.components[componentName] = this.componentShorttagsComponents[componentName];
    });
  },
  beforeMount() {
    return this.tiptapInitStaticWrapper(this.contentStatusDefault);
  },
  mounted() {
    // remind user that he is in preview mode
    if (this.previewDraftParam) {
      this.$bvToast.toast(this.$t('tiptap:draftPreviewMode.message'), {
        title: this.$t('tiptap:draftPreviewMode.title'),
        variant: 'info',
        appendToast: true,
        noAutoHide: true,
      });
    }
  },
  serverPrefetch() {
    return this.tiptapInitStaticWrapper(this.contentStatusDefault);
  },
  methods: {
    // browser close: show confirm dialog if there is unsaved content
    checkUnsavedContentBeforeunload(event) {
      // Cancel the event as stated by the standard.
      event.preventDefault();
      // Chrome requires returnValue to be set.
      event.returnValue = '';
    },
    // editor close: show confirm dialog if there is unsaved content on
    checkUnsavedContentEditorClose(contentStatus) {
      if (this.tiptapEditableChanged) {
        // eslint-disable-next-line no-alert
        const ignoreDraftChanges = window.confirm(this.$t('tiptap:draftAlert.unsavedDraftHeading'));
        if (ignoreDraftChanges) {
          // unlock editor and render static container
          this.tiptapContentLockToggle({ toggleState: 'unlock' })
            .finally(() => {
              this.tiptapInitStaticWrapper(contentStatus);
            });
          return true;
        }
        return false;
      }
      // just unlock editor and render static container if there was no unsaved content
      this.tiptapContentLockToggle({ toggleState: 'unlock' })
        .finally(() => {
          this.tiptapInitStaticWrapper(contentStatus);
        });
      return true;
    },
    tiptapInitEditableWrapper(contentStatus, overwrite = false) {
      // clear current tiptap instances before (re-) initializing
      if (this.tiptapEditable) {
        this.tiptapEditable.destroy();
      }
      this.tiptapEditable = null;
      this.tiptapStatic = null;

      this.tiptapEditable = this.tiptapEditableInit(contentStatus, overwrite);

      // lock tiptap editor for others
      this.tiptapContentLockToggle({ toggleState: 'lock' });
      return Promise.resolve();
    },
    tiptapInitStaticWrapper(contentStatus) {
      // clear current tiptap instances before (re-) initializing
      if (this.tiptapEditable) {
        this.tiptapEditable.destroy();
      }
      this.tiptapEditable = null;
      this.tiptapStatic = null;

      // always re-fetch static container for editors
      // for not-editors check store before fetching content from api
      if (this.userCanEdit || !this.tiptapContentStore?.[this.containerKey]?.[contentStatus]) {
        return this.tiptapContentGet({ previewDraft: this.previewDraftParam })
          .then(() => {
            this.tiptapStatic = this.tiptapStaticInit(contentStatus);
            return Promise.resolve();
          });
      }
      this.tiptapStatic = this.tiptapStaticInit(contentStatus);
      return Promise.resolve();
    },
    tiptapContentSetWrapper(contentStatus, contentAction) {
      this.tiptapContentSet({
        language: this.$store.getters.getCurrentLanguage,
        page: this.$router.currentRoute.path,
        contentStatus,
        contentAction,
        containerContent: this.tiptapEditable.getJSON(),
        // get updated editor data from API and re-init
      }).then(() => this.tiptapContentGet({ previewDraft: this.previewDraftParam }))
        .then(() => this.tiptapInitEditableWrapper(contentStatus))
        .then(() => this.tiptapContentDeleteWrapperClose());
    },
    tiptapContentDeleteWrapper() {
      this.$modal.show('tiptapContentDeleteModal');
    },
    tiptapContentDeleteWrapperClose() {
      this.$modal.hide('tiptapContentDeleteModal');
    },
    disableContentLinks() {
      // EDVUE-3634 prevent navigating away and losing unsaved content if tiptap editor is open
      if (this.tiptapEditable && typeof document !== 'undefined') {
        this.preventLinkClickTimeout = setTimeout(() => {
          document.querySelectorAll('.ProseMirror a').forEach((elem) => {
            if (elem) {
              elem.style = 'pointer-events: none;';
            }
          });
          this.preventLinkClickTimeout = false;
        }, 2000); // we have to wait for component html to be rendered
      }
    }
  },
};
</script>
<style lang="scss" src="scss/TiptapContainer.scss"></style>
